(ns ductile.ui.components.rect-selection
  (:require [nextjournal.clerk.render.hooks :as hooks]))

(defn- point-rect [x1 y1 x2 y2]
  {:left (min x1 x2)
   :top (min y1 y2)
   :right (max x1 x2)
   :bottom (max y1 y2)})

(defn- rect-overlap? [a b]
  (not (or (>= (:left a) (:right b))
           (>= (:top a) (:bottom b))
           (<= (:right a) (:left b))
           (<= (:bottom a) (:top b)))))

(defn- collect-item-boxes [^js container item-selector item-key-fn]
  (->> (.querySelectorAll container item-selector)
       array-seq
       (keep (fn [^js el]
               (when-let [k (item-key-fn el)]
                 (let [r (.getBoundingClientRect el)]
                   {:key k
                    :left (.-left r)
                    :top (.-top r)
                    :right (.-right r)
                    :bottom (.-bottom r)}))))
       vec))

(defn use-rect-selection
  [{:keys [container-ref
           enabled?
           item-selector
           item-key-fn
           start?
           on-preview
           on-commit
           min-distance]
    :or {enabled? true
         item-selector ".rect-select-item"
         item-key-fn (fn [^js el] (.getAttribute el "data-rect-key"))
         start? (constantly true)
         min-distance 4}}]
  (let [!dragging? (hooks/use-state false)
        !overlay-rect (hooks/use-state nil)
        !drag-ref (hooks/use-ref nil)
        !raf-id (hooks/use-ref nil)
        !pending-xy (hooks/use-ref nil)
        !suppress-click? (hooks/use-ref false)
        !on-preview-ref (hooks/use-ref on-preview)
        !on-commit-ref (hooks/use-ref on-commit)]
    (set! (.-current !on-preview-ref) on-preview)
    (set! (.-current !on-commit-ref) on-commit)
    (hooks/use-effect
     (fn []
       (let [render! (fn [{:keys [start-x start-y x y boxes append? active?]}]
                       (let [r (point-rect start-x start-y x y)
                             keys (->> boxes
                                       (keep (fn [box]
                                               (when (rect-overlap? r box)
                                                 (:key box))))
                                       set)]
                         (set! (.-current !drag-ref)
                               (assoc (.-current !drag-ref)
                                      :active? active?
                                      :x x
                                      :y y
                                      :keys keys))
                         (reset! !overlay-rect (when active? r))
                         (when-let [preview-fn (.-current !on-preview-ref)]
                           (preview-fn {:keys keys
                                        :append? append?}))))
             ensure-frame! (fn []
                             (when-not (.-current !raf-id)
                               (set! (.-current !raf-id)
                                     (js/requestAnimationFrame
                                      (fn []
                                        (set! (.-current !raf-id) nil)
                                        (when-let [{:keys [x y]} (.-current !pending-xy)]
                                          (set! (.-current !pending-xy) nil)
                                          (when-let [drag0 (.-current !drag-ref)]
                                            (let [moved? (> (+ (js/Math.abs (- x (:start-x drag0)))
                                                               (js/Math.abs (- y (:start-y drag0))))
                                                            min-distance)]
                                              (render! (assoc drag0
                                                              :x x
                                                              :y y
                                                              :active? (or (:active? drag0) moved?)))))))))))
             on-pointer-move (fn [^js event]
                               (when-let [_drag0 (.-current !drag-ref)]
                                 (.preventDefault event)
                                 (set! (.-current !pending-xy) {:x (.-clientX event)
                                                                :y (.-clientY event)})
                                 (ensure-frame!)))
             on-pointer-up (fn [_]
                             (when-let [drag0 (.-current !drag-ref)]
                               (when (:active? drag0)
                                 (set! (.-current !suppress-click?) true)
                                 (js/setTimeout #(set! (.-current !suppress-click?) false) 0))
                               (let [final-keys (or (:keys drag0) #{})]
                                 (when-let [commit-fn (and (:active? drag0) (.-current !on-commit-ref))]
                                   (commit-fn {:keys final-keys
                                               :append? (:append? drag0)}))))
                             (when-let [id (.-current !raf-id)]
                               (js/cancelAnimationFrame id))
                             (set! (.-current !raf-id) nil)
                             (set! (.-current !pending-xy) nil)
                             (set! (.-current !drag-ref) nil)
                             (reset! !overlay-rect nil)
                             (reset! !dragging? false))]
         (.addEventListener js/window "pointermove" on-pointer-move #js {:passive false})
         (.addEventListener js/window "pointerup" on-pointer-up)
         (.addEventListener js/window "pointercancel" on-pointer-up)
         #(do
            (.removeEventListener js/window "pointermove" on-pointer-move)
            (.removeEventListener js/window "pointerup" on-pointer-up)
            (.removeEventListener js/window "pointercancel" on-pointer-up)
            (when-let [id (.-current !raf-id)]
              (js/cancelAnimationFrame id))
            (set! (.-current !raf-id) nil)
            (set! (.-current !pending-xy) nil)
            (set! (.-current !drag-ref) nil)
            (reset! !overlay-rect nil)
            (reset! !dragging? false)
            (set! (.-current !suppress-click?) false))))
     [min-distance])
    {:on-pointer-down
     (fn [^js event]
       (when (and enabled?
                  (= 0 (.-button event))
                  (start? event))
         (when-let [^js container @container-ref]
           (let [boxes (collect-item-boxes container item-selector item-key-fn)]
             (set! (.-current !drag-ref)
                   {:start-x (.-clientX event)
                    :start-y (.-clientY event)
                    :x (.-clientX event)
                    :y (.-clientY event)
                    :append? (or (.-metaKey event) (.-ctrlKey event))
                    :boxes boxes
                    :keys #{}
                    :active? false})
             (reset! !dragging? true)))))
     :overlay-rect @!overlay-rect
     :consume-click? (fn []
                       (let [v (boolean (.-current !suppress-click?))]
                         (when v
                           (set! (.-current !suppress-click?) false))
                         v))}))
